import {
  MotifChip,
  MotifIconButton,
  MotifIcon,
  MotifTooltip,
} from "@ey-xd/motif-react";
import {
  IconoirEditPencil,
  IconoirBell,
  IconoirBellOff,
} from "@ey-xd/motif-icon";
import {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { EngagementDelegation as EngagementDelegationRow } from "../../../store/slices/EngagementDelegationSlice";

const getStatusChipClassName = (status: unknown): string => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "active") {
    return "active-status-chip";
  }
  if (normalizedStatus === "inactive") {
    return "inactive-status-chip";
  }
  return "";
};

export const getEngDelegationColumns = (
  onEdit: (row: EngagementDelegationRow) => void,
): ColDef<EngagementDelegationRow>[] => [
  {
    field: "EngagementId",
    headerName: "Engagement",
    headerClass: "engagement-delegation-header",
    headerComponentParams: {
      enableSorting: true,
      showFilter: false,
    },
    sortable: true,
    minWidth: 181,
    flex: 16,
    getQuickFilterText: (
      params: GetQuickFilterTextParams<EngagementDelegationRow>,
    ) => {
      return `${params.data?.EngagementId ?? ""} ${params.data?.EngagementName ?? ""}`;
    },
    cellRenderer: (params: ICellRendererParams<EngagementDelegationRow>) => {
      const engagementName = params.data?.EngagementName ?? "";
      return (
        <div className="engagement-delegation-primary-cell">
          <span>{params.data?.EngagementId}</span>
          <MotifTooltip
            allowClick={false}
            allowHover
            id={`tooltip-${params.data?.EngagementId}`}
            placement="auto"
            trigger={
              <span aria-label="Tooltip" className="secondary-text">
                {engagementName}
              </span>
            }
            usePortal
            variant="default"
          >
            {engagementName}
          </MotifTooltip>
        </div>
      );
    },
  },
  {
    field: "EngagementStatus",
    headerName: "Status",
    headerClass: "engagement-delegation-header",
    headerComponentParams: {
      enableSorting: true,
      showFilter: false,
    },
    sortable: true,
    initialSort: "asc",
    minWidth: 100,
    flex: 9,
    getQuickFilterText: () => "",
    cellRenderer: (params: ICellRendererParams<EngagementDelegationRow>) => {
      const status = params?.data?.EngagementStatus;
      const statusChipClassName = getStatusChipClassName(status);
      if (!status) {
        return "-";
      }
      return (
        <MotifChip className={statusChipClassName} title={status} type="chip">
          {status}
        </MotifChip>
      );
    },
  },
  {
    field: "EngagementOwnerName",
    headerName: "Engagement owner",
    headerClass: "engagement-delegation-header",
    headerComponentParams: {
      enableSorting: true,
      showFilter: false,
    },
    sortable: true,
    minWidth: 243,
    flex: 21,
    getQuickFilterText: (
      params: GetQuickFilterTextParams<EngagementDelegationRow>,
    ) => {
      return `${params.data?.EngagementOwnerName ?? ""} ${params.data?.EngagementOwnerEmail ?? ""}`;
    },
    cellRenderer: (params: ICellRendererParams<EngagementDelegationRow>) => {
      const row = params.data;
      return row ? (
        <div className="engagement-delegation-owner-cell">
          <div className="engagement-delegation-name">
            <MotifTooltip
              allowClick={false}
              allowHover
              id={`tooltip-${row.EngagementOwnerName}`}
              placement="top"
              trigger={
                <span
                  aria-label="Tooltip"
                  className="engagement-delegation-owner-name-tooltip-trigger"
                >
                  {row.EngagementOwnerName}
                </span>
              }
              usePortal
              variant="default"
            >
              {row.EngagementOwnerName}
            </MotifTooltip>
            <MotifTooltip
              placement="top"
              allowClick={false}
              allowHover
              hideCloseButton
              trigger={
                <MotifIcon
                  iconFunction={
                    Number(row.PartnerSendEmail) === 1
                      ? IconoirBell
                      : IconoirBellOff
                  }
                  size="16"
                  fill="#A4A3B1"
                />
              }
            >
              {Number(row.PartnerSendEmail) === 1
                ? "Notifications on"
                : "Notifications off"}
            </MotifTooltip>
          </div>
          <MotifTooltip
            allowClick={false}
            allowHover
            id={`tooltip-${row.EngagementOwnerEmail}`}
            placement="auto"
            trigger={
              <span aria-label="Tooltip" className="secondary-text">
                {row.EngagementOwnerEmail}
              </span>
            }
            usePortal
            variant="default"
          >
            {row.EngagementOwnerEmail}
          </MotifTooltip>
        </div>
      ) : null;
    },
  },
  {
    field: "IsAutoApprove",
    headerName: "Auto approve",
    headerClass: "engagement-delegation-header",
    headerComponentParams: {
      enableSorting: true,
      showFilter: false,
    },
    sortable: true,
    cellClass: "engagement-delegation-auto-approve-cell",
    minWidth: 136,
    flex: 12,
    getQuickFilterText: () => "",
  },
  {
    field: "AutoApproveAmount",
    headerName: "Approval threshold",
    headerComponentParams: {
      enableSorting: true,
      showFilter: false,
    },
    sortable: true,
    headerClass:
      "engagement-delegation-header engagement-delegation-header--right",
    minWidth: 144,
    flex: 13,
    cellClass: "engagement-delegation-amount-cell",
    getQuickFilterText: () => "",
    valueFormatter: (params: ValueFormatterParams<EngagementDelegationRow>) =>
      params.data?.IsAutoApprove.toLowerCase() === "no"
        ? "-"
        : params.value.toLocaleString("en-US", {
            currency: "USD",
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            style: "currency",
          }),
  },
  {
    field: "EngagementProxyDetails",
    headerName: "Engagement proxy",
    headerClass: "engagement-delegation-header",
    headerComponentParams: {
      enableSorting: false,
      showFilter: false,
    },
    sortable: false,
    minWidth: 240,
    getQuickFilterText: (
      params: GetQuickFilterTextParams<EngagementDelegationRow>,
    ) => {
      const proxies = params.data?.EngagementProxyDetails || [];
      return proxies.map((p) => `${p.Name} ${p.Email}`).join(" ");
    },
    cellRenderer: (params: ICellRendererParams<EngagementDelegationRow>) => {
      const proxies = params.data?.EngagementProxyDetails || [];
      if (!proxies.length) {
        return <span className="engagement-delegation-empty-value">--</span>;
      }

      const visibleProxies = proxies.slice(0, 1);
      const remainingProxies = proxies.slice(visibleProxies.length);
      const remainingProxyCount = remainingProxies.length;

      return (
        <div className="engagement-delegation-proxy-list">
          {visibleProxies.map((proxy) => (
            <div
              className="engagement-delegation-proxy-chip"
              key={proxy.UserId}
            >
              <div className="engagement-delegation-name">
                <MotifTooltip
                  allowClick={false}
                  allowHover
                  id={`tooltip-${proxy.Name}`}
                  placement="auto"
                  trigger={<span aria-label="Tooltip">{proxy.Name}</span>}
                  usePortal
                  variant="default"
                >
                  {proxy.Name}
                </MotifTooltip>
                <MotifIcon
                  iconFunction={
                    Number(proxy.DelegateSendEmail) === 1
                      ? IconoirBell
                      : IconoirBellOff
                  }
                  size="14"
                  fill="#A4A3B1"
                />
              </div>
              <MotifTooltip
                allowClick={false}
                allowHover
                id={`tooltip-${proxy.Email}`}
                placement="auto"
                trigger={
                  <span
                    aria-label="Tooltip"
                    className="engagement-delegation-proxy-email-trigger secondary-text"
                  >
                    {proxy.Email}
                  </span>
                }
                usePortal
                variant="default"
              >
                {proxy.Email}
              </MotifTooltip>
            </div>
          ))}
          {remainingProxyCount > 0 && (
            <MotifTooltip
              placement="top"
              allowClick
              allowHover={false}
              hideCloseButton
              contentClassName="engagement-delegation-proxy-tooltip"
              trigger={
                <span className="engagement-delegation-proxy-count">
                  +{remainingProxyCount}
                </span>
              }
            >
              <div className="engagement-delegation-proxy-tooltip-list">
                {remainingProxies.map((proxy) => (
                  <div
                    className="engagement-delegation-proxy-tooltip-chip"
                    key={proxy.UserId}
                  >
                    <div className="engagement-delegation-tooltip-name">
                      <span>{proxy.Name}</span>
                      <MotifIcon
                        iconFunction={
                          Number(proxy.DelegateSendEmail) === 1
                            ? IconoirBell
                            : IconoirBellOff
                        }
                        size="14"
                        fill="#A4A3B1"
                      />
                    </div>
                    <span className="engagement-delegation-tooltip-email">
                      {proxy.Email}
                    </span>
                  </div>
                ))}
              </div>
            </MotifTooltip>
          )}
        </div>
      );
    },
  },
  {
    headerName: "Actions",
    headerClass:
      "engagement-delegation-header engagement-delegation-header--center",
    headerComponentParams: {
      enableSorting: false,
      showFilter: false,
    },
    sortable: false,
    minWidth: 96,
    flex: 8,
    cellClass: "engagement-delegation-action-cell",
    cellRenderer: (params: ICellRendererParams<EngagementDelegationRow>) => (
      <div className="action-container">
        <MotifIconButton
          onClick={() => {
            if (params.data) onEdit(params.data);
          }}
          aria-label="Edit"
        >
          <MotifIcon
            size="20"
            iconFunction={IconoirEditPencil}
            className="action-icon"
          />
        </MotifIconButton>
      </div>
    ),
  },
];
